import React, { useEffect, useState, useMemo } from "react";
import { getFeeCategories } from "../../services/siteServices/siteCommertialsService";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Box,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  FormGroup,
  FormControl,
  FormLabel,
  InputLabel,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useParams } from "react-router-dom";
import {
  createCommercialBySiteId,
  deleteCommercialById,
  getCommercialsBySiteId,
} from "../../services/siteServices/commercialsServices";
import { getSiteById } from "../../services/siteServices/subSiteService";
import AppConfig from "../../config";
import DynamicTable from "./DynamicTable";
import { validateFields } from "../../helpers/utils";
import SnackBars from "../snackbars/SnackBars";

// Custom theme
const theme = createTheme({
  typography: {
    fontSize: 12,
  },
  components: {
    MuiTextField: {
      defaultProps: {
        variant: "outlined",
        size: "small",
      },
    },
    MuiSelect: {
      defaultProps: {
        variant: "outlined",
        size: "small",
      },
    },
    MuiButton: {
      defaultProps: {
        size: "small",
      },
    },
  },
});

const CommercialsDetailsTable = () => {
  const { id } = useParams();
  const [commercials, setCommercials] = useState([]);
  const [openAddDialog, setOpenAddDialog] = useState(false);

  // Separate states for feeCategory and amount
  const [feeCategory, setFeeCategory] = useState("");
  const [amount, setAmount] = useState("");
  const [type, setType] = useState("Estimated");

  const [errors, setErrors] = useState({
    feeCategory: false,
    amount: false,
  });

  // Fetch fee categories from the backend API
  const [feeCategories, setFeeCategories] = useState([]);

  const getAllCommercials = async (url) => {
    const res = await getCommercialsBySiteId(url);
    let temp = [];
    if (res.length > 0) {
      for (let i = 0; i < res.length; i++) {
        let obj = {
          id: res[i].id,
          index: i + 1,
          feeCategory: res[i].fee_category,
          amount: res[i].amount,
          type: res[i].type,
        };
        temp.push(obj);
      }
    }
    setCommercials(temp);
  };

  const getSite = async (url) => {
    const res = await getSiteById(url);
    const rate = res.ProjectDetails.package_rate || 0;
    const totalArea = res.assetsDetails.reduce((accumulator, currentObject) => {
      return accumulator + Number(currentObject.DisplayArea);
    }, 0);
    return rate * totalArea || 0;
  };

  // Fetch fee categories from the service
  const fetchFeeCategories = async () => {
    try {
      const categories = await getFeeCategories(); // Call the service function

      setFeeCategories(categories); // Set fee categories to the state
    } catch (error) {
      console.error("Error fetching fee categories:", error);
    }
  };

  useEffect(() => {
    getSite(id);
    getAllCommercials(id);
    fetchFeeCategories(); // Fetch fee categories when the component mounts
  }, [id]);

  const handleOpenAddDialog = () => {
    setOpenAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setFeeCategory("");
    setAmount("");
    setType("");
  };

  const handleFeeCategoryChange = async (e) => {
    const selectedCategory = e.target.value;
    setFeeCategory(selectedCategory);

    // Validate fields so that helper text will go
    validateFields(feeCategory, selectedCategory, setErrors);

    if (selectedCategory === "OMC Rentals") {
      const fetchedAmount = await getSite(id);
      if (fetchedAmount !== 0) {
        setAmount(fetchedAmount);
      } else {
        setAmount(""); // Let the user input manually if fetchedAmount is 0
      }
    } else {
      setAmount(""); // Reset amount for other categories
    }
  };

  // const handleAmountChange = (event) => {
  //   setAmount(event.target.value);
  //   validateFields("amount", event.target.value, setErrors);
  // };
  const handleAmountChange = (event) => {
    const value = event.target.value;

    // Validate if the value is >= 1 and is not 0
    if (value === "" || /^[1-9]\d*$/.test(value)) {
      setAmount(value);
      validateFields("amount", value, setErrors); // Assuming validateFields performs further validation
      setErrors((prevErrors) => ({
        ...prevErrors,
        amount: "", // Clear error when valid
      }));
    } else {
      setErrors((prevErrors) => ({
        ...prevErrors,
        amount: "Amount must be greater than 0.", // Set error message
      }));
    }
  };

  const handleSaveNewCommercial = async () => {
    try {
      if (!feeCategory) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          feeCategory: "Fee category is required",
        }));
        return;
      }
      if (!amount) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          amount: "Amount is required",
        }));
        return;
      }
      const postData = {
        feeCategory,
        amount,
        type,
      };
      await createCommercialBySiteId(id, postData);
      getAllCommercials(id);
      handleCloseAddDialog();
      setOpenSnackBar(true);
      setSnackBarMessage({
        title: "Commercial Detail added successfully",
        type: "success",
      });
    } catch (error) {
      console.log("handleSaveNewCommercial error", error);
    }
  };

  const handleDeleteCommercial = async (idToDelete) => {
    try {
      await deleteCommercialById(idToDelete);
      getAllCommercials(id);
      setOpenSnackBar(true);
      setSnackBarMessage({
        title: "Commercial Detail Deleted Succesfully",
        type: "success",
      });
    } catch (error) {
      console.log("handleDeleteCommercial error", error);
    }
  };

  const calculateTotalCost = (commercials) => {
    return commercials.reduce(
      (total, item) => total + parseFloat(item.amount || 0),
      0
    );
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "id",
        header: "Action",
        size: 50,
        Cell: ({ row }) => (
          <Typography
            sx={{
              cursor: "pointer",
              fontSize: "14px",
              color: "#1976D2",
              "&:hover": {
                color: "#1976D2",
              },
            }}
            onClick={() => handleDeleteCommercial(row.original.id)}
          >
            <DeleteIcon sx={{ color: AppConfig.colorPalette.error.dark }} />
          </Typography>
        ),
      },
      {
        accessorKey: "index",
        header: "Serial No",
        size: 50,
      },
      {
        accessorKey: "feeCategory",
        header: "Fee Category",
        size: 100,
      },
      {
        accessorKey: "amount",
        header: "Amount (₹)",
        size: 100,
      },
      {
        accessorKey: "type",
        header: "Type",
        size: 100,
      },
    ],
    []
  );

  const [openSnackBar, setOpenSnackBar] = useState(false);
  const [snackBarMessage, setSnackBarMessage] = useState({
    title: "",
    type: "",
  });

  const handleCloseSnackBar = () => {
    setOpenSnackBar(false);
  };

  return (
    <ThemeProvider theme={theme}>
      <div style={{ width: "100%", margin: "auto" }}>
        {commercials.length > 0 ? (
          <DynamicTable columns={columns} data={commercials} />
        ) : (
          <Typography>
            No commercials have been added yet. Please click here to add
            commercials.
          </Typography>
        )}

        {/* Add New Commercial Dialog */}
        <Button
          onClick={handleOpenAddDialog}
          variant="contained"
          sx={{ margin: "16px 0", backgroundColor: "#b41660", color: "#fff" }}
        >
          Add New Commercial
        </Button>
        <Dialog
          fullWidth={true}
          maxWidth="sm"
          open={openAddDialog}
          onClose={handleCloseAddDialog}
        >
          <DialogTitle>Add New Commercial</DialogTitle>
          <DialogContent>
            <FormControl fullWidth margin="normal">
              <InputLabel id="feeCategory">
                Fee Category{" "}
                <Typography
                  component="span"
                  sx={{ color: "red", marginLeft: "4px" }}
                >
                  *
                </Typography>
              </InputLabel>
              <Select
                margin="normal"
                fullWidth
                label="Fee Category"
                labelId="feeCategory"
                value={feeCategory}
                sx={{
                  paddingBottom: "10px",
                  border: errors.feeCategory ? "2px solid red" : "",
                }}
                onChange={handleFeeCategoryChange}
              >
                {feeCategories.map((category, index) => (
                  <MenuItem key={index} value={category.name}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.feeCategory && (
                <Typography color="error" variant="caption">
                  {errors.feeCategory}
                </Typography>
              )}
            </FormControl>
            <TextField
              fullWidth
              label={
                <>
                  Amount
                  <Typography
                    component="span"
                    sx={{ color: "red", marginLeft: "4px" }}
                  >
                    *
                  </Typography>
                </>
              }
              value={amount}
              onChange={handleAmountChange}
              error={!!errors.amount}
              helperText={errors.amount}
              margin="normal"
            />
            <FormControl component="fieldset">
              <FormLabel component="legend">Type</FormLabel>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={type === "Estimated"}
                      onChange={() => setType("Estimated")}
                    />
                  }
                  label="Estimated"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={type === "Actual"}
                      onChange={() => setType("Actual")}
                    />
                  }
                  label="Actual"
                />
              </FormGroup>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAddDialog}>Cancel</Button>
            <Button onClick={handleSaveNewCommercial}>Save</Button>
          </DialogActions>
        </Dialog>

        {/* SnackBar for success message */}
        <SnackBars
          open={openSnackBar}
          message={snackBarMessage.title}
          type={snackBarMessage.type}
          onClose={handleCloseSnackBar}
        />
      </div>
    </ThemeProvider>
  );
};

export default CommercialsDetailsTable;
